import { useState, useEffect, useRef } from 'react';
import { ProductDetail, ProductSpec } from '@/mocks/productDetails';
import ImageManager from './ImageManager';

interface ProductFormModalProps {
  product: ProductDetail | null;
  onSave: (data: Omit<ProductDetail, 'id'>) => void;
  onClose: () => void;
  saving?: boolean;
}

const CATEGORIES = ['PS5', 'PS4', 'PC', 'Controllers', 'Headsets', 'Accessories'];

const EMPTY: Omit<ProductDetail, 'id'> = {
  nameAr: '',
  category: 'PS5',
  badge: '',
  rating: 5,
  reviews: 0,
  price: 0,
  originalPrice: 0,
  stock: 10,
  shortDesc: '',
  longDesc: '',
  images: [],
  highlights: [''],
  specs: [{ label: '', value: '' }],
  inBox: [''],
  relatedIds: [],
};

interface FieldError {
  nameAr?: string;
  price?: string;
  images?: string;
}

export default function ProductFormModal({ product, onSave, onClose, saving: externalSaving }: ProductFormModalProps) {
  const [form, setForm] = useState<Omit<ProductDetail, 'id'>>(EMPTY);
  const [errors, setErrors] = useState<FieldError>({});
  const [section, setSection] = useState<'basic' | 'media' | 'details'>('basic');
  const nameRef = useRef<HTMLInputElement>(null);

  const isSaving = externalSaving ?? false;

  useEffect(() => {
    if (product) {
      const { id, ...rest } = product;
      void id;
      setForm(rest);
    } else {
      setForm(EMPTY);
    }
    setErrors({});
    setSection('basic');
    setTimeout(() => nameRef.current?.focus(), 100);
  }, [product]);

  const set = <K extends keyof typeof form>(key: K, val: typeof form[K]) =>
    setForm(prev => ({ ...prev, [key]: val }));

  // Highlights
  const setHighlight = (i: number, val: string) => {
    const u = [...form.highlights]; u[i] = val;
    setForm(prev => ({ ...prev, highlights: u }));
  };
  const addHighlight = () => setForm(prev => ({ ...prev, highlights: [...prev.highlights, ''] }));
  const removeHighlight = (i: number) => setForm(prev => ({ ...prev, highlights: prev.highlights.filter((_, idx) => idx !== i) }));

  // Specs
  const setSpec = (i: number, field: keyof ProductSpec, val: string) => {
    const u = [...form.specs]; u[i] = { ...u[i], [field]: val };
    setForm(prev => ({ ...prev, specs: u }));
  };
  const addSpec = () => setForm(prev => ({ ...prev, specs: [...prev.specs, { label: '', value: '' }] }));
  const removeSpec = (i: number) => setForm(prev => ({ ...prev, specs: prev.specs.filter((_, idx) => idx !== i) }));

  // InBox
  const setInBox = (i: number, val: string) => {
    const u = [...form.inBox]; u[i] = val;
    setForm(prev => ({ ...prev, inBox: u }));
  };
  const addInBox = () => setForm(prev => ({ ...prev, inBox: [...prev.inBox, ''] }));
  const removeInBox = (i: number) => setForm(prev => ({ ...prev, inBox: prev.inBox.filter((_, idx) => idx !== i) }));

  const validate = (): boolean => {
    const errs: FieldError = {};
    if (!form.nameAr.trim()) errs.nameAr = 'اسم المنتج مطلوب';
    if (!form.price || form.price <= 0) errs.price = 'السعر يجب أن يكون أكبر من 0';
    if (form.images.length === 0) errs.images = 'أضف صورة واحدة على الأقل';
    setErrors(errs);
    if (errs.nameAr || errs.price) { setSection('basic'); return false; }
    if (errs.images) { setSection('media'); return false; }
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave({
      ...form,
      highlights: form.highlights.filter(h => h.trim()),
      specs: form.specs.filter(s => s.label.trim() && s.value.trim()),
      inBox: form.inBox.filter(i => i.trim()),
    });
  };

  const SECTIONS = [
    { key: 'basic',   label: 'الأساسية', icon: 'ri-information-line',  hasError: !!(errors.nameAr || errors.price) },
    { key: 'media',   label: 'الصور',    icon: 'ri-image-line',         hasError: !!errors.images },
    { key: 'details', label: 'التفاصيل', icon: 'ri-list-check-2',       hasError: false },
  ] as const;

  const discountPct = form.originalPrice > form.price && form.originalPrice > 0
    ? Math.round(((form.originalPrice - form.price) / form.originalPrice) * 100)
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" dir="rtl">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a] rounded-xl">
              <i className={`${product ? 'ri-edit-line' : 'ri-add-line'} text-white text-base`}></i>
            </div>
            <div>
              <h2 className="font-bold text-[#1a1a1a] text-base leading-tight">
                {product ? 'تعديل المنتج' : 'إضافة منتج جديد'}
              </h2>
              {product && (
                <p className="text-gray-400 text-xs mt-0.5 truncate max-w-[200px]">{product.nameAr}</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* ── Section Tabs ── */}
        <div className="flex gap-1 px-6 pt-3 pb-0 flex-shrink-0">
          {SECTIONS.map(s => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSection(s.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-xl transition-all cursor-pointer whitespace-nowrap border-b-2 ${
                section === s.key
                  ? 'border-[#1a1a1a] text-[#1a1a1a] bg-gray-50'
                  : s.hasError
                  ? 'border-red-400 text-red-500 hover:bg-red-50'
                  : 'border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className={`${s.icon} text-sm`}></i>
              {s.label}
              {s.hasError && <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0"></span>}
            </button>
          ))}
        </div>
        <div className="h-px bg-gray-100 mx-6 flex-shrink-0"></div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* ═══ BASIC ═══ */}
          {section === 'basic' && (
            <div className="space-y-5">

              {/* Name + Category row */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">
                    اسم المنتج <span className="text-red-500">*</span>
                  </label>
                  <input
                    ref={nameRef}
                    type="text"
                    value={form.nameAr}
                    onChange={e => { set('nameAr', e.target.value); if (errors.nameAr) setErrors(p => ({ ...p, nameAr: undefined })); }}
                    placeholder="مثال: بلايستيشن 5 ديجيتال"
                    className={`w-full border rounded-xl px-3 py-2.5 text-sm outline-none transition-colors ${errors.nameAr ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-gray-200 focus:border-[#1a1a1a]'}`}
                  />
                  {errors.nameAr && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.nameAr}</p>}
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">الفئة <span className="text-red-500">*</span></label>
                  <select
                    value={form.category}
                    onChange={e => set('category', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer bg-white"
                  >
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Price row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">
                    السعر الحالي (ر.س) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={form.price || ''}
                      onChange={e => { set('price', Number(e.target.value)); if (errors.price) setErrors(p => ({ ...p, price: undefined })); }}
                      placeholder="0"
                      min="0"
                      className={`w-full border rounded-xl px-3 py-2.5 text-sm outline-none transition-colors ${errors.price ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-gray-200 focus:border-[#1a1a1a]'}`}
                    />
                  </div>
                  {errors.price && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i>{errors.price}</p>}
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">
                    السعر الأصلي (ر.س)
                    <span className="text-gray-400 font-normal mr-1">— للخصم</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={form.originalPrice || ''}
                      onChange={e => set('originalPrice', Number(e.target.value))}
                      placeholder="0"
                      min="0"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                    />
                    {discountPct > 0 && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        -{discountPct}%
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Stock */}
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5">
                  المخزون المتاح
                  <span className={`mr-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    form.stock === 0 ? 'bg-red-100 text-red-600' :
                    form.stock <= 5 ? 'bg-amber-100 text-amber-700' :
                    'bg-emerald-100 text-emerald-700'
                  }`}>
                    {form.stock === 0 ? 'نفد المخزون' : form.stock <= 5 ? `كمية محدودة (${form.stock})` : `متوفر (${form.stock})`}
                  </span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={form.stock || ''}
                    onChange={e => set('stock', Math.max(0, Number(e.target.value)))}
                    placeholder="0"
                    min="0"
                    className="w-28 border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                  />
                  <div className="flex gap-1.5 flex-wrap">
                    {[0, 5, 10, 25, 50, 100].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => set('stock', n)}
                        className={`px-3 py-1.5 text-xs rounded-lg border transition-colors cursor-pointer whitespace-nowrap font-medium ${
                          form.stock === n
                            ? 'bg-[#1a1a1a] text-white border-[#1a1a1a]'
                            : 'border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-400'
                        }`}
                      >
                        {n === 0 ? 'نفد' : n}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Badge */}
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5">
                  الشارة
                  <span className="text-gray-400 font-normal mr-1">— تظهر على بطاقة المنتج (اختياري)</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={form.badge}
                    onChange={e => set('badge', e.target.value)}
                    placeholder="مثال: جديد، الأكثر مبيعاً، خصم 20%"
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                  />
                  {form.badge && (
                    <span className="bg-amber-400 text-black text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap">
                      {form.badge}
                    </span>
                  )}
                </div>
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {['جديد', 'الأكثر مبيعاً', 'عرض محدود', 'حصري'].map(b => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => set('badge', form.badge === b ? '' : b)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                        form.badge === b ? 'bg-amber-400 border-amber-400 text-black font-bold' : 'border-gray-200 text-gray-500 hover:border-gray-400'
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>

              {/* Short desc */}
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5">وصف مختصر</label>
                <input
                  type="text"
                  value={form.shortDesc}
                  onChange={e => set('shortDesc', e.target.value)}
                  placeholder="جملة قصيرة تصف المنتج — تظهر في قائمة المنتجات"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                />
              </div>

              {/* Long desc */}
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5">وصف تفصيلي</label>
                <textarea
                  value={form.longDesc}
                  onChange={e => set('longDesc', e.target.value)}
                  placeholder="وصف كامل للمنتج يظهر في صفحة التفاصيل..."
                  rows={3}
                  maxLength={500}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors resize-none"
                />
                <p className="text-xs text-gray-300 text-left mt-0.5">{form.longDesc.length}/500</p>
              </div>

              {/* Rating */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">التقييم</label>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => set('rating', n)}
                        className={`text-xl cursor-pointer transition-colors ${n <= form.rating ? 'text-amber-400' : 'text-gray-200 hover:text-amber-200'}`}
                      >
                        <i className="ri-star-fill"></i>
                      </button>
                    ))}
                    <span className="text-xs text-gray-400 mr-1">{form.rating}/5</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">عدد التقييمات</label>
                  <input
                    type="number"
                    value={form.reviews || ''}
                    onChange={e => set('reviews', Number(e.target.value))}
                    placeholder="0"
                    min="0"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ═══ MEDIA ═══ */}
          {section === 'media' && (
            <div>
              {errors.images && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
                  <i className="ri-error-warning-line text-red-500 text-sm"></i>
                  <p className="text-red-600 text-sm font-medium">{errors.images}</p>
                </div>
              )}
              <p className="text-xs text-gray-400 mb-3">
                الصورة الأولى تظهر كصورة رئيسية في قوائم المنتجات. يمكنك رفع صور من جهازك أو إضافة روابط.
              </p>
              <ImageManager
                images={form.images}
                onChange={imgs => { set('images', imgs); if (errors.images && imgs.length > 0) setErrors(p => ({ ...p, images: undefined })); }}
              />
            </div>
          )}

          {/* ═══ DETAILS ═══ */}
          {section === 'details' && (
            <div className="space-y-6">

              {/* Highlights */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-gray-600 flex items-center gap-1.5">
                    <i className="ri-check-double-line text-green-500"></i>
                    أبرز المميزات
                    <span className="text-gray-400 font-normal">— تظهر في صفحة المنتج</span>
                  </label>
                  <button type="button" onClick={addHighlight} className="text-xs text-[#1a1a1a] font-bold hover:underline cursor-pointer whitespace-nowrap flex items-center gap-1">
                    <i className="ri-add-line"></i>إضافة
                  </button>
                </div>
                <div className="space-y-2">
                  {form.highlights.map((h, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <div className="w-5 h-5 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                        <i className="ri-check-line text-green-600 text-xs"></i>
                      </div>
                      <input
                        type="text"
                        value={h}
                        onChange={e => setHighlight(i, e.target.value)}
                        placeholder={`ميزة ${i + 1} — مثال: شاشة OLED بدقة 4K`}
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                      />
                      <button type="button" onClick={() => removeHighlight(i)} className="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0 rounded-lg hover:bg-red-50">
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Specs */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-gray-600 flex items-center gap-1.5">
                    <i className="ri-table-line text-amber-500"></i>
                    المواصفات التقنية
                  </label>
                  <button type="button" onClick={addSpec} className="text-xs text-[#1a1a1a] font-bold hover:underline cursor-pointer whitespace-nowrap flex items-center gap-1">
                    <i className="ri-add-line"></i>إضافة صف
                  </button>
                </div>
                <div className="space-y-2">
                  {form.specs.map((spec, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={spec.label}
                        onChange={e => setSpec(i, 'label', e.target.value)}
                        placeholder="الخاصية — مثال: المعالج"
                        className="w-2/5 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-gray-50"
                      />
                      <input
                        type="text"
                        value={spec.value}
                        onChange={e => setSpec(i, 'value', e.target.value)}
                        placeholder="القيمة — مثال: AMD Ryzen 7"
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                      />
                      <button type="button" onClick={() => removeSpec(i)} className="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0 rounded-lg hover:bg-red-50">
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* InBox */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-gray-600 flex items-center gap-1.5">
                    <i className="ri-gift-line text-amber-500"></i>
                    محتويات الصندوق
                  </label>
                  <button type="button" onClick={addInBox} className="text-xs text-[#1a1a1a] font-bold hover:underline cursor-pointer whitespace-nowrap flex items-center gap-1">
                    <i className="ri-add-line"></i>إضافة
                  </button>
                </div>
                <div className="space-y-2">
                  {form.inBox.map((item, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <div className="w-5 h-5 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                        <i className="ri-box-3-line text-amber-600 text-xs"></i>
                      </div>
                      <input
                        type="text"
                        value={item}
                        onChange={e => setInBox(i, e.target.value)}
                        placeholder={`عنصر ${i + 1} — مثال: كابل HDMI`}
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                      />
                      <button type="button" onClick={() => removeInBox(i)} className="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0 rounded-lg hover:bg-red-50">
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50/50 rounded-b-2xl">
          {/* Validation summary */}
          <div className="flex items-center gap-2">
            {Object.keys(errors).length > 0 ? (
              <div className="flex items-center gap-1.5 text-red-500 text-xs">
                <i className="ri-error-warning-line text-sm"></i>
                <span>أكمل الحقول المطلوبة</span>
              </div>
            ) : form.nameAr && form.price > 0 && form.images.length > 0 ? (
              <div className="flex items-center gap-1.5 text-green-600 text-xs">
                <i className="ri-checkbox-circle-line text-sm"></i>
                <span>جاهز للحفظ</span>
              </div>
            ) : (
              <p className="text-gray-400 text-xs">
                {!form.nameAr ? 'أدخل اسم المنتج' : !form.price ? 'أدخل السعر' : 'أضف صورة واحدة على الأقل'}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap rounded-lg hover:bg-gray-100"
            >
              إلغاء
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-[#1a1a1a] text-white rounded-xl px-6 py-2.5 text-sm font-bold hover:bg-[#333] disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              {isSaving ? (
                <><i className="ri-loader-4-line animate-spin"></i>جاري الحفظ...</>
              ) : (
                <><i className={product ? 'ri-save-line' : 'ri-add-circle-line'}></i>{product ? 'حفظ التعديلات' : 'إضافة المنتج'}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
